import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import {
    Button,
    Checkbox,
    Field,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    SearchableDropdown,
} from "modules/common";
import React, { useEffect, useState } from "react";
import { PhytoviewEntry, PhytoviewRequest } from "services/phytoview";
import { defaultISelectOption } from "types/selectLabel";
import { IPhytoViewModalProps } from "views";
import {
    UseCreatePhytoViewEntry,
    UseEditPhytoViewEntry,
    useGetGlavneFazeAsItems,
    useGetOnePhytoView,
} from "../../hooks/phytoViewHook";

const emptyPhytoView: PhytoviewRequest = {
    name: "",
    description: "",
    active: false,
    nameLat: "",
};

const validateFenofazePodfazeRecord = (
    phytoview: PhytoviewRequest,
    glavneFaze: { label: string; id: number | null }
): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!phytoview.name.trim()) {
        errors.name = "Naziv je obavezan";
    }
    if (glavneFaze?.id == null) {
        errors.glavneFaze = "Glavna faza je obavezna";
    }
    return errors;
};

const PhytoViewFenofazePodfazeModal: React.FC<IPhytoViewModalProps> = ({
    title,
    isOpen,
    onClose,
    isPost,
    editItemId,
}) => {
    const endpoint = "FenofazePodfaze";
    const [phytoview, setPhytoview] = useState<PhytoviewRequest>(emptyPhytoView);
    const [, setIsEdit] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const { data: dataOne } = useGetOnePhytoView(endpoint, editItemId);
    const create = UseCreatePhytoViewEntry(endpoint);
    const edit = UseEditPhytoViewEntry(endpoint, editItemId);
    const [kultura, setKultura] = useState<{ label: string; id: number | null }>({
        label: "",
        id: null,
    });

    const glavneFazeOptions = useGetGlavneFazeAsItems();
    const [glavneFaze, setGlavneFaze] = useState<{ label: string; id: number | null }>({
        label: "",
        id: null,
    });

    const inputReset = () => {
        setPhytoview({ ...emptyPhytoView });
        setKultura({ label: "", id: null });
        setErrors({});
    };

    useEffect(() => {
        if (!isPost && isOpen && dataOne) {
            setPhytoview(prev => ({
                ...prev,
                active: dataOne.active ?? true,
                name: dataOne.name ?? "",
                description: dataOne.description ?? "",
                nameLat: dataOne.nameLat ?? "",
            }));
            if (
                dataOne.vrstaId &&
                typeof dataOne.vrstaId !== "number" &&
                dataOne.vrstaId.predmetOpazanjaId &&
                typeof dataOne.vrstaId.predmetOpazanjaId !== "number"
            ) {
                setKultura({
                    label: dataOne.vrstaId?.predmetOpazanjaId?.name ?? "",
                    id: dataOne.vrstaId?.predmetOpazanjaId?.id ?? null,
                });
            }
            if (dataOne.vrstaId && typeof dataOne.vrstaId !== "number") {
                setGlavneFaze({
                    label: `${dataOne.vrstaId.name ?? ""} - ${
                        typeof dataOne.vrstaId.predmetOpazanjaId !== "number"
                            ? dataOne.vrstaId.predmetOpazanjaId?.name ?? ""
                            : "N/A"
                    }`,
                    id: dataOne.vrstaId.id ?? null,
                });
            }
        } else {
            inputReset();
        }
    }, [isPost, isOpen, dataOne]);

    if (!isOpen) return null;

    const onCancel = () => {
        inputReset();
        setIsEdit(false);
        setIsFormChanged(false);
        onClose();
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        setPhytoview(prev => ({ ...prev, active: checked }));
        setIsFormChanged(true);
    };

    const isValid = (property: string) => {
        return !errors[property];
    };

    const getErrorMsg = (property: string) => {
        return errors[property] ?? "";
    };

    const onSave = async () => {
        const newErrors = validateFenofazePodfazeRecord(phytoview, glavneFaze);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }
        setErrors({});

        const payload: PhytoviewEntry = {
            name: phytoview.name,
            description: phytoview.description,
            active: phytoview.active,
            parentId: glavneFaze.id,
            jls: [],
            naziv: kultura.label,
            created: "",
            createdBy: "",
            lastUpdated: "",
            lastUpdatedBy: "",
            id: 0,
            vrstaId: glavneFaze.id,
            predmetOpazanjaId: kultura.id,
        };
        const mutation = isPost ? create.mutateAsync : edit.mutateAsync;
        await mutation(payload);
        inputReset();
        setIsFormChanged(false);
        onClose();
    };

    return (
        <Modal
            isActive={isOpen}
            className="phytoview-details-modal"
            onClose={onClose}
            modalHeader={
                <ModalHeader onClose={onClose} title={title} className="phytoview-modal-header" />
            }
            modalFooter={
                <ModalFooter>
                    <div className="phytoview-footer">
                        <Button
                            type="button"
                            size="small"
                            variant="primary-ghost"
                            onClick={onCancel}
                        >
                            <span>ODUSTANI</span>
                        </Button>
                        <Button
                            type="button"
                            size="small"
                            variant="primary"
                            disabled={!isFormChanged}
                            onClick={onSave}
                        >
                            <span>SPREMI</span>
                        </Button>
                    </div>
                </ModalFooter>
            }
        >
            <div className="phytoview-details">
                <div className="modal-section">
                    <Field name="NAZIV" errorMessage={getErrorMsg("name")}>
                        <Input
                            size="small"
                            name="name_input_filed"
                            id="name_id"
                            type="text"
                            isValid={isValid("name")}
                            value={phytoview?.name}
                            onChange={e => {
                                setPhytoview({ ...phytoview, name: e.target.value });
                                setIsFormChanged(true);
                            }}
                        />
                    </Field>
                </div>

                <div className="modal-section">
                    <Field name="OPIS">
                        <Input
                            size="large"
                            name="desc_input_filed"
                            id="desc_id"
                            type="text"
                            isValid
                            value={phytoview.description}
                            onChange={e => {
                                setPhytoview({ ...phytoview, description: e.target.value });
                                setIsFormChanged(true);
                            }}
                            rows={3}
                        />
                    </Field>
                </div>

                <div className="modal-section">
                    <Field name="AKTIVAN">
                        <Checkbox
                            isChecked={phytoview.active}
                            type="checkbox"
                            htmlFor="example-checkbox"
                            text="Da"
                            onChange={handleCheckboxChange}
                            className="custom-checkbox"
                            value="checkbox-value"
                            isReversed
                        />
                    </Field>
                </div>

                <div className="modal-section">
                    <Field name="GLAVNA FAZA" errorMessage={getErrorMsg("glavneFaze")}>
                        <SearchableDropdown
                            items={glavneFazeOptions}
                            value={glavneFaze}
                            noOptionsMessage={() => "Nema pronađenih glavnih faza"}
                            labelKey="label"
                            onSelect={item => {
                                setGlavneFaze(item ?? defaultISelectOption);
                                setIsFormChanged(true);
                            }}
                            placeholder="Pretraživanje"
                            customIcons={{ searchIcon: <SearchIcon width={14} height={14} /> }}
                            isValid={isValid("glavneFaze")}
                        />
                    </Field>
                </div>
            </div>
        </Modal>
    );
};

export default PhytoViewFenofazePodfazeModal;
